/**
 * CaseConverter - Service
 * Converts between various text case formats
 * 
 * Single Responsibility: Transform text between case formats
 */

type CaseFormat = 
  | 'camelCase' 
  | 'PascalCase' 
  | 'snake_case' 
  | 'kebab-case' 
  | 'CONSTANT_CASE'
  | 'Train-Case'
  | 'dot.case'
  | 'path/case'
  | 'lower case'
  | 'UPPER CASE'
  | 'Title Case'
  | 'no case'
  | 'Sentence case'
  | 'mOcKiNg CaSe';

interface ConversionResult {
  original: string;
  result: string;
  from: CaseFormat | 'auto';
  to: CaseFormat;
}

class CaseConverter {
  private static readonly WORD_SEPARATORS = /[\s\-_./]+|(?<=[a-z])(?=[A-Z])|(?<=[A-Z])(?=[A-Z][a-z])/;

  /**
   * Convert text from one case to another
   */
  convert(text: string, to: CaseFormat, from?: CaseFormat): ConversionResult {
    if (!text || text.trim().length === 0) {
      return { original: text, result: '', from: from ?? 'auto', to };
    }

    const detectedFrom = from ?? this.detectFormat(text);
    const words = this.extractWords(text);
    const result = this.formatWords(words, to);

    return {
      original: text,
      result,
      from: detectedFrom,
      to
    };
  }

  /**
   * Convert to camelCase
   */
  toCamelCase(text: string): string {
    return this.convert(text, 'camelCase').result;
  }

  /**
   * Convert to PascalCase
   */
  toPascalCase(text: string): string {
    return this.convert(text, 'PascalCase').result;
  }

  /**
   * Convert to snake_case
   */
  toSnakeCase(text: string): string {
    return this.convert(text, 'snake_case').result;
  }

  /**
   * Convert to kebab-case
   */
  toKebabCase(text: string): string {
    return this.convert(text, 'kebab-case').result;
  }

  /**
   * Convert to CONSTANT_CASE
   */
  toConstantCase(text: string): string {
    return this.convert(text, 'CONSTANT_CASE').result;
  }

  /**
   * Detect the case format of text
   */
  detectFormat(text: string): CaseFormat | 'auto' {
    if (text.includes('_')) {
      return text === text.toUpperCase() ? 'CONSTANT_CASE' : 'snake_case';
    }
    if (text.includes('-')) {
      const firstChar = text[0];
      return firstChar && firstChar === firstChar.toUpperCase() ? 'Train-Case' : 'kebab-case';
    }
    if (text.includes('.')) {
      return 'dot.case';
    }
    if (text.includes('/')) {
      return 'path/case';
    }
    if (text.includes(' ')) {
      if (text === text.toUpperCase()) return 'UPPER CASE';
      if (text === text.toLowerCase()) return 'lower case';
      if (text[0] === text[0]?.toUpperCase() && text.slice(1) === text.slice(1).toLowerCase()) {
        return 'Sentence case';
      }
      return 'Title Case';
    }
    const firstChar = text[0];
    if (firstChar && firstChar === firstChar.toUpperCase()) {
      return 'PascalCase';
    }
    return 'camelCase';
  }

  /**
   * Extract words from any case format
   */
  private extractWords(text: string): string[] {
    return text
      .split(CaseConverter.WORD_SEPARATORS)
      .filter((word): word is string => !!word && word.length > 0)
      .map(word => word.toLowerCase());
  }

  /**
   * Format words according to target case
   */
  private formatWords(words: string[], format: CaseFormat): string {
    if (words.length === 0) return '';
    
    switch (format) {
      case 'camelCase':
        return words[0]! + words.slice(1).map(this.capitalize).join('');
      
      case 'PascalCase':
        return words.map(this.capitalize).join('');
      
      case 'snake_case':
        return words.join('_');
      
      case 'kebab-case':
        return words.join('-');
      
      case 'CONSTANT_CASE':
        return words.join('_').toUpperCase();
      
      case 'Train-Case':
        return words.map(this.capitalize).join('-');
      
      case 'dot.case':
        return words.join('.');
      
      case 'path/case':
        return words.join('/');
      
      case 'lower case':
        return words.join(' ');
      
      case 'UPPER CASE':
        return words.join(' ').toUpperCase();
      
      case 'Title Case':
        return words.map(this.capitalize).join(' ');
      
      case 'no case':
        return words.join(' ');
      
      case 'Sentence case':
        if (words.length === 0) return '';
        return this.capitalize(words[0]!) + words.slice(1).join(' ');
      
      case 'mOcKiNg CaSe':
        return words.map((word, i) => 
          word.split('').map((char, j) => 
            (i + j) % 2 === 0 ? char.toLowerCase() : char.toUpperCase()
          ).join('')
        ).join(' ');
      
      default:
        return words.join('');
    }
  }

  private capitalize(word: string): string {
    return word.charAt(0).toUpperCase() + word.slice(1);
  }

  /**
   * Batch convert multiple strings
   */
  batchConvert(texts: string[], to: CaseFormat): ConversionResult[] {
    return texts.map(text => this.convert(text, to));
  }

  /**
   * Get all available formats for a text
   */
  getAllFormats(text: string): Record<CaseFormat, string> {
    const formats: CaseFormat[] = [
      'camelCase',
      'PascalCase',
      'snake_case',
      'kebab-case',
      'CONSTANT_CASE',
      'Train-Case',
      'dot.case',
      'path/case',
      'lower case',
      'UPPER CASE',
      'Title Case',
      'no case',
      'Sentence case',
      'mOcKiNg CaSe'
    ];

    const result = {} as Record<CaseFormat, string>;
    for (const format of formats) {
      result[format] = this.convert(text, format).result;
    }
    return result;
  }
}

export { CaseConverter };
export type { CaseFormat, ConversionResult };
